import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BoardResponse,
  BoardColumn,
  TaskCard,
  TaskCrudResponse,
  TaskPriority,
} from '../services/api/board';
import { updateTask } from '../services/api/board';

export interface UpdateTaskInput {
  projectId: string;
  taskId: number;
  name?: string;
  description?: string | null;
  budget?: number | null;
  hardLimit?: boolean;
  plannedHours?: number | null;
  priority?: TaskPriority; // MKS-163
  /**
   * MKS-341 — the due date as a calendar day (`YYYY-MM-DD`). `undefined`
   * leaves it alone; explicit `null` CLEARS it, which is why the guards below
   * test `!== undefined` rather than truthiness — `null` must reach the wire.
   */
  dueDate?: string | null;
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

/**
 * Phase 24 Plan 24-08 — internal task edit (name + description).
 * Patches the matching task row in-place across all columns by id.
 * Rolls back to the pre-mutation snapshot on error.
 *
 * Cache key locked to ['board', projectId, 'internal'] (D-12).
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation<TaskCrudResponse, Error, UpdateTaskInput, MutationContext>({
    mutationFn: (input) => {
      const body: {
        name?: string;
        description?: string | null;
        budget?: number | null;
        hardLimit?: boolean;
        plannedHours?: number | null;
        priority?: TaskPriority;
        dueDate?: string | null; // MKS-341
      } = {};
      if (input.name !== undefined) body.name = input.name;
      if (input.description !== undefined) body.description = input.description;
      if (input.budget !== undefined) body.budget = input.budget;
      if (input.hardLimit !== undefined) body.hardLimit = input.hardLimit;
      if (input.plannedHours !== undefined) body.plannedHours = input.plannedHours;
      if (input.priority !== undefined) body.priority = input.priority;
      if (input.dueDate !== undefined) body.dueDate = input.dueDate; // MKS-341
      return updateTask(input.taskId, body);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['board', input.projectId, 'internal'] });
      const previous = queryClient.getQueryData<BoardResponse>([
        'board',
        input.projectId,
        'internal',
      ]);
      if (previous) {
        queryClient.setQueryData<BoardResponse>(
          ['board', input.projectId, 'internal'],
          applyOptimisticUpdate(previous, input),
        );
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, 'internal'], context.previous);
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, 'internal'],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Pure helper — exported for unit testing.
// ---------------------------------------------------------------------------

export function applyOptimisticUpdate(
  board: BoardResponse,
  input: UpdateTaskInput,
): BoardResponse {
  const next: BoardColumn[] = board.columns.map((col) => ({
    ...col,
    tasks: col.tasks.map(
      (t): TaskCard =>
        t.id === input.taskId
          ? {
              ...t,
              ...(input.name !== undefined ? { name: input.name } : {}),
              ...(input.description !== undefined ? { description: input.description } : {}),
              ...(input.budget !== undefined ? { budget: input.budget } : {}),
              ...(input.hardLimit !== undefined ? { hardLimit: input.hardLimit } : {}),
              ...(input.plannedHours !== undefined ? { plannedHours: input.plannedHours } : {}),
              ...(input.priority !== undefined ? { priority: input.priority } : {}),
              // MKS-341 — `null` is a real optimistic value (the date was
              // cleared), so the guard is `!== undefined`, not truthiness.
              ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
            }
          : t,
    ),
  }));
  return { columns: next };
}
